'use client'

import { ConversionRateSection, EmergencyMessage, HighlightSection, HomePageVisual, LoggedNavigation, PromotionsSection, SearchSection, SidebarWrapper } from '@/components/uiComponents'
import AgentService from '@/features/agent/agent-service'
import LoggedUserRefetchQuery from '@/features/auth/components/LoggedUserRefetchQuery'
import useLoggedUser from '@/features/auth/hooks/useLoggedUser'
import useCurrentMarket from '@/features/caching/hooks/useCurrentMarket'
import { Highlight } from '@/features/sitecore/actions/highlights'
import { Info } from '@/features/sitecore/actions/infos'
import { MarketingTool } from '@/features/sitecore/actions/marketing-tools'
import { New } from '@/features/sitecore/actions/news'
import { Promotion } from '@/features/sitecore/actions/promotions'
import { TranslationBundles } from '@/features/sitecore/configs/translation-bundles'
import { useSitecoreState } from '@/features/sitecore/store/useSitecoreState'
import { calculateTimeLeft } from '@/utils/analytics/googleAnalyticsHelper'
import { useModalState } from '@/utils/hooks/useModalState'
import { useUserEntryModals } from '@/utils/hooks/useUserEntryModals'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

interface HomePageProps {
	promotionsHero: Promotion[] | []
	promotionsNormals: Promotion[] | []
	highlighted: Highlight[] | []
	news: New[] | []
	info: Info[] | []
	marketingTools: MarketingTool[] | []
}

const HomePage = (props: HomePageProps) => {
	const router = useRouter()
	const pathname = usePathname()
	const params = useSearchParams()
	const user = useLoggedUser()
	const modal = useModalState()
	const market = useCurrentMarket()
	useUserEntryModals()

	const sitecoreStore = useSitecoreState((state) => state)
	const templateValue = sitecoreStore.getNumber(TranslationBundles.HOME, 'MSC-HOMEPAGE-TEMPLATE_NUMBER', 1, [1, 2, 3])

	const removeQueryParam = (param: string) => {
		const urlWithoutParam = `${pathname}?${params}`.replace(new RegExp(`&?${param}=([^&]*)&?`), '')
		router.replace(urlWithoutParam)
	}

	if (params.get('login')?.toLowerCase() === 'true') {
		removeQueryParam('login')
		if (!user) modal.openLoginModal()
	}

	const emergencyMessageTitleLabel = sitecoreStore.getLabel(TranslationBundles.HOME, 'MSC-EMERGENCY-MESSAGE-TEXT-TITLE', 'Emergency message')
	const emergencyMessageDescriptionLabel = sitecoreStore.getLabel(TranslationBundles.HOME, 'MSC-EMERGENCY-MESSAGE-TEXT-EMERGENCY', 'Emergency message content')
	const showEmergencyMessage = sitecoreStore.getLabel(TranslationBundles.HOME, 'MSC-EMERGENCY-MESSAGE-FLAG', 'No')
	const highlightedTitleLabel = sitecoreStore.getLabel(TranslationBundles.WELCOME, 'MSC-EXTERNAL-WELCOMEPAGE-HIGHLIGHTED-PROMOTION', 'Highlighted Promotion')

	// GA4
	const sendViewPromotionsGA4Event = () => {
		window.dataLayer = window.dataLayer || []
		window.dataLayer.push({ ecommerce: null })
		const ecommerceObj: any = { items: [] }
		let creativeSlotCounter = 1
		props.promotionsHero?.map((promotion) => {
			return ecommerceObj.items.push({
				promotion_id: promotion.bundleName?.split('_').pop(),
				promotion_name: promotion.title,
				creative_name: promotion.title,
				creative_slot: creativeSlotCounter++,
				location_id: 'Header',
				time_left: calculateTimeLeft(promotion.expireDate),
				section: user ? 'Private' : 'Public',
			})
		})
		props.promotionsNormals?.map((promotion) => {
			return ecommerceObj.items.push({
				promotion_id: promotion.bundleName?.split('_').pop(),
				promotion_name: promotion.title,
				creative_name: promotion.title,
				creative_slot: creativeSlotCounter++,
				location_id: 'Body',
				time_left: calculateTimeLeft(promotion.expireDate),
				section: user ? 'Private' : 'Public',
			})
		})

		window.dataLayer.push({
			event: 'view_promotion',
			eventName: 'view_promotion',
			user_id: user?.agency || '',
			network_id_u: user?.consortiumCode || '',
			agency_code_u: user?.agency || '',
			ecommerce: { ...ecommerceObj },
		})
	}

	useEffect(() => {
		sendViewPromotionsGA4Event()
	}, [])

	useEffect(() => {
		if (!user) {
			AgentService.deleteVoyagerCardCookie()
		}
	}, [user])
	// GA4
	return (
		<>
			<LoggedUserRefetchQuery />

			<div className={`flex flex-col`}>
				<div className="relative">
					{user && showEmergencyMessage.toLowerCase() === 'yes' && (
						<div className={`${user ? 'xl:w-[calc(100%-6rem)] 2xl:w-[calc(100%-8rem)] xl:ml-24 2xl:ml-32' : 'w-full'}`}>
							<EmergencyMessage title={emergencyMessageTitleLabel} description={emergencyMessageDescriptionLabel} link="#" />
						</div>
					)}
					{user && <LoggedNavigation />}
				</div>

				<div className={`flex flex-1 w-full flex-col gap-6 md:gap-12 ${user ? `xl:w-[calc(100%-6rem)] 2xl:w-[calc(100%-8rem)] ${templateValue === 1 ? 'xl:pb-24' : 'xl:p-8 xl:pb-24'} xl:ml-24 2xl:ml-32` : 'w-full'} pb-20 md:pb-40`}>
					{props.promotionsHero?.length > 0 && (
						<div className={`${templateValue === 2 ? 'order-2' : 'order-1'} ${templateValue === 3 ? 'hidden' : 'block'}`}>
							<HomePageVisual promotions={props.promotionsHero} />
						</div>
					)}

					{user && (
						<div className={`px-6 ${templateValue === 2 ? 'order-1' : 'order-2'} ${user && templateValue === 1 ? 'xl:px-8' : ''} ${templateValue !== 1 ? 'pt-4' : ''}`}>
							<SearchSection />
						</div>
					)}
					{user && market.locale === 'ar' && <ConversionRateSection />}
					<div className={`container ${!user ? 'px-6' : 'px-6 xl:max-w-full'} w-full flex flex-col xl:flex-row mx-auto gap-6 ${user && templateValue === 1 ? 'xl:px-8' : ''} ${templateValue === 3 ? 'pt-6' : ''} order-3`}>
						<div className="flex flex-col w-full gap-14">
							{props.highlighted?.length > 0 && <HighlightSection title={highlightedTitleLabel} highlights={props.highlighted} />}
							{(props.promotionsNormals?.length ?? 0) > 0 && <PromotionsSection promotions={templateValue === 3 ? [...(props.promotionsHero ?? []), ...(props.promotionsNormals ?? [])] : (props.promotionsNormals ?? [])} />}
						</div>
						<div className="xl:min-w-96 xl:max-w-96">
							<SidebarWrapper news={props.news} infos={props.info} marketingTools={props.marketingTools} />
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default HomePage
